import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import {
	TokenRefreshService,
	type TokenRefreshResult,
} from "@Front/Services/TokenRefreshService/TokenRefreshService";
import { CustomerStore } from "@Front/Stores/CustomerStore";
import { ThirdPartyStore } from "@Front/Stores/ThirdPartyStore";
import { UserStore } from "@Front/Stores/UserStore";
import type { AnyJwtPayload } from "@Front/Types/JwtPayloads";
import { jwtDecode } from "@Front/Utils/jwtDecodeWrapper";
import Auth from "@Front/Api/Auth/Customer/Auth";
import { resolveActiveAuthContextFromSession, resolveActiveRefreshTokenFromSession } from "@Front/Api/helpers/authTokenResolutionHelpers";
import { resolveNextRefreshDelayMs, resolveRefreshContext } from "./sessionRefreshDelayHelpers";
import {
	disconnectSessionStoreFromPayload,
	runSessionFailureCleanup,
	runUnauthenticatedAccessCleanup,
} from "./sessionRefreshFailureHelpers";
import { cleanupStoreSubscriptions, createSessionRefreshStoreSubscriptions } from "./sessionRefreshStoreSubscriptions";
import type { LeCoffreAuthContext } from "@Front/Utils/lecoffreSessionCookieHelpers";
import { isNetworkError } from "@Front/Utils/errorClassifiers";

const REFRESH_MARGIN_MS = 2 * 60 * 1000; // 2 minutes avant expiration
const MIN_DELAY_MS = 30 * 1000;
const FALLBACK_DELAY_MS = 10 * 60 * 1000;

class SessionRefreshService {
	private static _instance: SessionRefreshService | null = null;
	private timer: number | null = null;
	private isStarted = false;
	private unsubscribeFns: Array<() => void> = [];

	public static getInstance(): SessionRefreshService {
		if (!SessionRefreshService._instance) {
			SessionRefreshService._instance = new SessionRefreshService();
		}
		return SessionRefreshService._instance;
	}

	public start(): void {
		if (typeof window === "undefined" || this.isStarted) {
			return;
		}

		this.isStarted = true;
		this.subscribeToStores();
		if (this.shouldStayPassiveOnPublicLoginEntry()) {
			LoggerService.debug("ℹ️ [SessionRefresh] Service armed (public login entry)");
			return;
		}
		const hasActiveSession = resolveActiveAuthContextFromSession() !== null;
		if (!hasActiveSession) {
			LoggerService.debug("ℹ️ [SessionRefresh] Service armed (no active session yet)");
			return;
		}
		LoggerService.info("🔁 [SessionRefresh] Auto-refresh service started");
		this.scheduleNextRefresh();
	}

	public stop(): void {
		if (!this.isStarted) {
			return;
		}
		this.clearTimer();
		this.unsubscribeFromStores();
		this.isStarted = false;
		LoggerService.info("⏹️ [SessionRefresh] Auto-refresh service stopped");
	}

	private subscribeToStores(): void {
		const schedule = (): void => this.scheduleNextRefresh();
		const idle = (): void => this.scheduleRetry();
		this.unsubscribeFns.push(...createSessionRefreshStoreSubscriptions(schedule, idle));
	}

	private unsubscribeFromStores(): void {
		cleanupStoreSubscriptions(this.unsubscribeFns);
	}

	private clearTimer(): void {
		if (this.timer !== null) {
			clearTimeout(this.timer);
			this.timer = null;
		}
	}

	private scheduleNextRefresh(): void {
		this.clearTimer();
		if (this.shouldStayPassiveOnPublicLoginEntry()) {
			LoggerService.debug("ℹ️ [SessionRefresh] Public login entry detected, waiting for authenticated session");
			return;
		}

		const refreshToken = resolveActiveRefreshTokenFromSession();
		if (!refreshToken) {
			if (resolveActiveAuthContextFromSession() === null) {
				LoggerService.debug("ℹ️ [SessionRefresh] No active session, waiting for connect event");
				return;
			}
			LoggerService.info("ℹ️ [SessionRefresh] No refresh token available, retry scheduled");
			this.scheduleRetry();
			return;
		}

		const payload = this.safeDecode(refreshToken);
		if (!payload) {
			this.scheduleRetry();
			return;
		}

		const delay = resolveNextRefreshDelayMs(
			payload,
			{
				decodeJwtPayload: (token) => this.safeDecode(token),
				refreshMarginMs: REFRESH_MARGIN_MS,
				minDelayMs: MIN_DELAY_MS,
				fallbackDelayMs: FALLBACK_DELAY_MS,
			},
		);

		LoggerService.info("⏳ [SessionRefresh] Next refresh scheduled", { delayMs: delay });
		this.timer = window.setTimeout(async () => {
			const shouldContinue = await this.refreshNow(refreshToken, payload);
			if (shouldContinue) {
				this.scheduleNextRefresh();
			}
		}, delay);
	}

	private scheduleRetry(delay: number = FALLBACK_DELAY_MS): void {
		this.clearTimer();
		if (this.shouldStayPassiveOnPublicLoginEntry()) {
			return;
		}
		this.timer = window.setTimeout(() => this.scheduleNextRefresh(), delay);
	}

	private shouldStayPassiveOnPublicLoginEntry(): boolean {
		if (typeof window === "undefined") {
			return false;
		}
		const hasConnectedStore =
			UserStore.instance.isConnected() || CustomerStore.instance.isConnected() || ThirdPartyStore.instance.isConnected();
		if (hasConnectedStore) {
			return false;
		}
		const pathname = window.location.pathname;
		return pathname === "/" || pathname.startsWith("/customers/login") || pathname.startsWith("/third-party/login");
	}

	private safeDecode(token: string): AnyJwtPayload | null {
		try {
			return jwtDecode<AnyJwtPayload>(token);
		} catch (error) {
			LoggerService.warn("⚠️ [SessionRefresh] Unable to decode refresh token", { error });
			return null;
		}
	}

	private async refreshTokenByPayload(refreshToken: string, payload: AnyJwtPayload): Promise<TokenRefreshResult | null> {
		const context = resolveRefreshContext(payload);
		if (context === "thirdparty") {
			return TokenRefreshService.refreshThirdPartyToken(refreshToken);
		}
		if (context === "customer") {
			return TokenRefreshService.refreshCustomerToken(refreshToken);
		}
		if (context === "notary") {
			return TokenRefreshService.refreshUserToken(refreshToken);
		}
		LoggerService.warn("⚠️ [SessionRefresh] Unsupported refresh token payload", { payload });
		return null;
	}

	private async applyRefreshResult(result: TokenRefreshResult | null, payload: AnyJwtPayload): Promise<boolean> {
		if (result === null) {
			return true;
		}
		if (result.success) {
			LoggerService.info("✅ [SessionRefresh] Token refreshed successfully");
			return true;
		}
		if (result.failureKind === "network" || result.failureKind === "transient") {
			LoggerService.warn("⚠️ [SessionRefresh] Token refresh failed (transient/network) — keeping session", {
				failureKind: result.failureKind,
				httpStatus: result.httpStatus,
			});
			return true;
		}
		const httpStatus = result.httpStatus;
		const isCriticalError = httpStatus === 401 || httpStatus === 403;
		if (isCriticalError) {
			LoggerService.error("❌ [SessionRefresh] Critical error during token refresh, session will be terminated", {
				httpStatus,
				reason: httpStatus === 401 ? "Unauthorized - token invalid or expired" : "Forbidden - access denied",
			});
			await this.handleSessionFailure(payload);
			return false;
		}
		LoggerService.warn("⚠️ [SessionRefresh] Token refresh failed — keeping session", {
			httpStatus,
			failureKind: result.failureKind,
		});
		return true;
	}

	/**
	 * Attempts to refresh the token
	 * @returns true if the refresh service should continue scheduling, false if session failed and should stop
	 */
	private async refreshNow(refreshToken: string, payload: AnyJwtPayload | null): Promise<boolean> {
		if (!payload) {
			return true;
		}
		try {
			const result = await this.refreshTokenByPayload(refreshToken, payload);
			return await this.applyRefreshResult(result, payload);
		} catch (error) {
			LoggerService.error("❌ [SessionRefresh] Unexpected error during token refresh", { error });
			if (isNetworkError(error)) {
				LoggerService.warn("⚠️ [SessionRefresh] Network error during refresh — keeping session");
				return true;
			}
			if (payload) {
				await this.handleSessionFailure(payload);
				return false;
			}
			return true;
		}
	}

	private async revokeLeCoffreToken(): Promise<void> {
		try {
			await Auth.getInstance().logout();
			LoggerService.info("✅ [SessionRefresh] Token LeCoffre révoqué côté serveur");
		} catch (error) {
			LoggerService.error("❌ [SessionRefresh] Erreur lors de la révocation du token LeCoffre", { error });
		}
	}

	/**
	 * Public method to handle session failure from external sources (e.g., API errors)
	 * @param payload - JWT payload from refresh token
	 */
	public async handleSessionFailure(payload: AnyJwtPayload): Promise<void> {
		await runSessionFailureCleanup(payload, {
			stop: () => this.stop(),
			revokeLeCoffreToken: () => this.revokeLeCoffreToken(),
			disconnectSessionStore: disconnectSessionStoreFromPayload,
		});
	}

	/**
	 * When cookies/stores are already empty (401 with no Authorization / no refresh token),
	 * still leave the protected SPA instead of ignoring the failure (zombie UI).
	 */
	public async handleUnauthenticatedAccess(context: LeCoffreAuthContext): Promise<void> {
		await runUnauthenticatedAccessCleanup(context, () => this.stop());
	}
}

// Export named only - no default export to avoid Webpack initialization order issues
export { SessionRefreshService };
